import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { useCreateConversation } from '../../hooks/useConversations';
import { useToast } from '../toast/Toast';

/**
 * Responsive shell:
 *  - Desktop (≥ lg, 1024px+): sidebar fixed left (320px), content fills the rest.
 *  - Tablet (≥ sm, 640-1023px): same layout, narrower sidebar.
 *  - Mobile (< sm): sidebar hidden behind hamburger menu, content fills viewport.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const create = useCreateConversation();
  const toast = useToast();

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Global keyboard shortcuts
  useEffect(() => {
    function isMod(e: KeyboardEvent): boolean {
      return navigator.platform.toLowerCase().includes('mac') ? e.metaKey : e.ctrlKey;
    }

    function handler(e: KeyboardEvent) {
      // Don't hijack typing in inputs / textareas / editors
      const target = e.target as HTMLElement | null;
      const inInput =
        target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

      // Cmd/Ctrl+K (search) is intentionally disabled in v1 — SearchPanel is
      // hidden per product direction v2. Re-enable both here and in Sidebar
      // when Search lands in v2.

      if (isMod(e) && e.shiftKey && e.key.toLowerCase() === 'o') {
        // Cmd/Ctrl+Shift+O — new conversation (avoids clobbering Cmd+N "new window")
        e.preventDefault();
        if (!create.isPending) {
          void create.mutateAsync('cfo').then((r) => {
            navigate(`/c/${r.conversation.id}`);
            toast.show('New conversation created.', 'success');
          });
        }
        return;
      }

      if (e.key === 'Escape' && mobileOpen) {
        setMobileOpen(false);
        return;
      }

      if (e.key === '?' && !inInput && e.shiftKey) {
        e.preventDefault();
        toast.show(
          'Shortcuts: Cmd/Ctrl+Shift+O new chat · Cmd/Ctrl+/ focus composer · Esc close menu',
          'info',
        );
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [create, navigate, toast, mobileOpen]);

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Mobile top bar */}
      <div className="sm:hidden fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-3">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="btn-ghost h-10 min-h-0 px-2"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
        >
          <MenuIcon />
        </button>
        <span className="text-sm font-semibold text-slate-800">Walker Blue Hub</span>
        <span className="w-10" aria-hidden="true" />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="sm:hidden fixed inset-0 z-50 bg-slate-900/40"
          onClick={() => setMobileOpen(false)}
          role="presentation"
        >
          <div
            className="absolute inset-y-0 left-0 w-[85%] max-w-xs bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Desktop / tablet sidebar */}
      <div className="hidden sm:flex sm:w-64 lg:w-80 shrink-0">
        <Sidebar />
      </div>

      <main className="flex-1 min-w-0 flex flex-col pt-14 sm:pt-0">{children}</main>
    </div>
  );
}

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}
